/*
 * Copyright (c) 2023-2024 SmartWifi - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential. No warranty, explicit or implicit provided. In
 * no event will the author be liable for any claim or damages.
 */

import { useContext, useEffect, useState } from "react";
import { Button, Label } from "reactstrap";
import { requestCaptivePortalPostLoginPreview } from "../../../../context/RequestManager";
import { AccountContext, ContextUpdater, CreateLoginPageContext } from "../../../../context/ContextProvider";
import { getLoginLocalization } from "../../../../util/Localization";
import FormInput, { getId } from "../../../../provisioning/elements/FormInput";
import GridContainer from "../../../../elements/GridContainer";
import LayoutImageForm from "../../../layouts/layoutEditForm/LayoutImageForm";
import ThemeSwitch from "../../../../elements/ThemeSwitch";
import ModalTextEditor, { defaultHTML } from "../../../../elements/ModalTextEditor";
import LayoutButtonGroupForm from "../../../layouts/layoutEditForm/LayoutButtonGroupForm";
import SplitField from "../../../../provisioning/elements/SplitField";
import LayoutColorForm from "../../../layouts/layoutEditForm/LayoutColorForm";

export const parseDefaultHtml = (html) => {
  // handle no html
  if (!html) {
    return null;
  }

  // parse html
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  
  const titleElement = doc.getElementById('defaultTitle') ?? doc.querySelector('h1'); // h1 for backward compatibility
  const title = titleElement?.textContent ?? "";
  let titleSizeStyle = defaultTextSize.md;

  let font = "Rubik";
  let fontColor = "#000000";

  if (titleElement) {
    titleElement.attributes['style']?.value?.split(';').forEach((item) => {
      if (item.includes('font-size')) {
        titleSizeStyle = item.replace(/[^\d.]/g, '');
      }

      // TODO: Fix - disabling for now to default to "Rubik"
      // if (item.includes('font-family')) {
      //   const [, fontFamily="Rubik"] = item.split(":").map((ele) => ele.replace('!important', '').trim());
      //   font = fontFamily;
      // }

      if (item.includes('color')) {
        const [, color="#000000"] = item.split(":").map((ele) => ele.replace('!important', '').trim());
        fontColor = color;
      }
    });
  }
  
  const bodyElement = doc.getElementById('defaultBody') ?? doc.querySelector('p');
  const body = bodyElement?.textContent ?? "";
  let bodySizeStyle = defaultTextSize.sm;
  if (bodyElement) {
    bodyElement.attributes['style']?.value?.split(';').forEach((item) => {
      if (item.includes('font-size')) {
        bodySizeStyle = item.replace(/[^\d.]/g, '');
      }

      // TODO: Fix - disabling for now to default to "Rubik"
      // if (item.includes('font-family')) {
      //   const [, fontFamily="Rubik"] = item.split(":").map((ele) => ele.replace('!important', '').trim());
      //   font = fontFamily;
      // }

      if (item.includes('color')) {
        const [, color="#000000"] = item.split(":").map((ele) => ele.replace('!important', '').trim());
        fontColor = color;
      }
    });
  }
  
  const imageElement = doc.querySelector('img');
  const image = imageElement ? imageElement.getAttribute('src') : null;
  
  let titleSize = "md";
  let bodySize = "md";

  Object.entries(defaultTextSize).forEach(([key, value]) => {
    if (value === titleSizeStyle) {
      titleSize = key;
    }

    if (value === bodySizeStyle) { 
      bodySize = key;
    }
  })

  return { title, body, image, titleSize, bodySize, font, fontColor };
};

const defaultTextSize = {
  xs: "0.5",
  sm: "1.25",
  md: "2.25",
  lg: "3.25",
  xl: "4.25",
}

const PostLoginPageSubstep = (props) => {
  // constants
  const { name: clientName } = props.client ?? {};

  // contexts
  const updateContext = useContext(ContextUpdater);
  const accountContext = useContext(AccountContext);
  const createLoginPageContext = useContext(CreateLoginPageContext);

  // modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [defaultState, setDefaultState] = useState({ body: null});

  // get path
  const fullPath = getId(props);
  const typeKey = `${fullPath}.type`;
  const htmlKey = `${fullPath}.html`;
  const defaultKey = `${fullPath}.default`;
  const defaultTitleKey = `${fullPath}.defaultTitle`;
  const defaultBodyKey = `${fullPath}.defaultBody`;
  const defaultImageKey = `${fullPath}.defaultImage`;
  const defaultTitleSizeKey = `${fullPath}.defaultTitleSize`;
  const defaultBodySizeKey = `${fullPath}.defaultBodySize`;
  const defaultFontKey = `${fullPath}.defaultFont`;
  const defaultFontColorKey = `${fullPath}.defaultFontColor`;
  
  // read and write
  const { name, watch, setValue } = props;
  const formProps = { ...props, path: name };

  // set initial state
  useEffect(() => {
    setValue(typeKey, null);
    setValue(defaultTitleKey, "You're Connected!");
    setValue(defaultBodyKey, `Thank you for connecting to ${clientName ?? "our"} WiFi!`);
    setValue(defaultTitleSizeKey, "md");
    setValue(defaultBodySizeKey, "sm");
    setValue(defaultFontKey, "Rubik");
    setValue(defaultFontColorKey, "#000000")
    
    setDefaultState({body:`Thank you for connecting to ${clientName ?? "our"} WiFi!`})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // skip if already loaded
    if (loaded) {
      return;
    }

    // select initial type if available
    const initialType = watch(typeKey);
    if (initialType) {
      handleSelected(initialType);
    }
  });

  // read current state
  const state = watch(fullPath) ?? {};
  const { type, html, defaultImage } = state;

  // button types
  const postLoginTypes = {
    facebook: { text: "Follow Us on Facebook", icon: "facebook" },
    instagram: { text: "Follow on Instragram", icon: "instagram" },
    default: { text: "Default Landing Page", icon: "layout-text-window" },
    video: { text: "YouTube Video", icon: "youtube" },
    html: { text: "Custom HTML", icon: "code-slash" },
  };

  // handlers
  const setPostLoginType = (type) => {
    createLoginPageContext.updateState({ postLoginPreviewType: type });
    setValue(typeKey, type);
  };

  const handleDefaultUpdated = (newValues = {}) => {
    // prepare attributes
    const defaultAttributes = {
      title: state.defaultTitle,
      body: state.defaultBody,
      image: state.defaultImage,
      titleSize: state.defaultTitleSize,
      bodySize: state.defaultBodySize,
      font: state.defaultFont,
      fontColor: state.defaultFontColor,
      ...newValues,
    }

    // Verify rows
    const rows = 20;
    const lines = defaultAttributes.body?.split('\n');
    const newLineValid = lines.length <= rows;
    if (!newLineValid) {
      defaultAttributes.body = defaultState.body;
    }

    const titleSize = `${defaultTextSize[defaultAttributes.titleSize] ?? defaultTextSize.md}rem`;
    const bodySize = `${defaultTextSize[defaultAttributes.bodySize] ?? defaultTextSize.md}rem`;

    // parse default html
    const renderedDefault = 
    `<div style="padding-top:20%;display:flex;flex-direction:column;justify-content:center;align-items:center;">      
      ${defaultAttributes.image
        ? `<img style="max-width:80%;max-height:50%;" src=${defaultAttributes.image} alt="" />`
        : `<div style="max-width:80%;max-height:50%;></div>"`
      }
      <br />
      <p id="defaultTitle" style="text-align: center; font-size: ${titleSize}; font-family: ${defaultAttributes.font} !important; color: ${defaultAttributes.fontColor};">${defaultAttributes.title}</p>
      <br />
      <p id="defaultBody" style="text-align: center; font-size: ${bodySize}; font-family: ${defaultAttributes.font} !important; color: ${defaultAttributes.fontColor}; white-space: pre-line;">${defaultAttributes.body}</p>
    <div>`;

    // update form
    setValue(defaultTitleSizeKey, defaultAttributes.titleSize);
    setValue(defaultBodySizeKey, defaultAttributes.bodySize);
    setValue(defaultBodyKey, defaultAttributes.body);
    setValue(defaultFontKey, defaultAttributes.font);
    setValue(defaultFontColorKey, defaultAttributes.fontColor);
    setValue(defaultKey, renderedDefault);

    setDefaultState({ body: defaultAttributes.body })
  };

  const handleSelected = (typeId) => {
    setLoaded(true);
    switch (typeId) {
      case null:
        // clear preview
        createLoginPageContext.updateState({
          postLoginPreview: null,
          postLoginPreviewType: null
        });
        setValue(typeKey, null);
        break;
      case "default":
        // parse default html
        handleDefaultUpdated();
        setPostLoginType("default");
        break;
      case "html":
        // prepare raw html
        if (html == null) {
          setValue(htmlKey, defaultHTML);  
        }
        setPostLoginType("html");
        break;
      default:
        // request new preview html
        let language = watch("localization.default");
        if (!language) {
          language = getLoginLocalization().getLanguage();
        }
        requestCaptivePortalPostLoginPreview({
          context: updateContext,
          token: accountContext.token,
          type: typeId,
          language: language,
          completion: (response) => {
            setValue(typeKey, typeId);
          }
        });
        break;
    }
  };

  return (
    <div>
      <ThemeSwitch
        text="Enable the Post-Login Page"
        checked={type != null}
        onChange={(checked) => handleSelected(checked ? "default" : null)}
      /> 
      <GridContainer width="220">
        {Object.keys(postLoginTypes).map((typeId, index) => {
          const {text, icon} = postLoginTypes[typeId];
          return (
            <div key={index} className="p-1">
              <Button
                key={index}
                className="fill-container"
                color={type == null ? "secondary" : "info"}
                outline={typeId !== type}
                onClick={() => handleSelected(typeId)}
                disabled={type == null}
              >
                <Label className="pt-1" tag="h5">{text}</Label>
                <Label tag="h3"><i className={`icon bi bi-${icon}`} /></Label>
              </Button>
            </div>
          )
        })}
      </GridContainer>
      <div className={`w-100 ${type === "facebook" ? "d-flex align-items-center" : "d-none"}`}>
        <Label className="ps-3 pt-2 flex-static">Facebook URL: </Label>
        <FormInput {...formProps} className="p-3 flex-dynamic" placeholder="https://www.facebook.com/..." name="facebookUrl" maxChars={1024} />
      </div>
      <div className={`w-100 ${type === "instagram" ? "d-flex align-items-center" : "d-none"}`}>
        <Label className="ps-3 pt-2 flex-static">Instagram profile name: </Label>
        <FormInput {...formProps} className="p-3 flex-dynamic" name="instagramProfile" />
      </div>
      <div className={`w-100 ${type === "default" ? "d-block" : "d-none"}`}>
      <div className="px-3 pt-3">
          <LayoutImageForm
            config={defaultImage}
            onContentChange={(image) => {
              handleDefaultUpdated({ image: image });
              setValue(defaultImageKey, image);
            }}
          />
        </div>
        <Label className="px-3 pt-2 flex-static">Title Text: </Label>
        <SplitField className='px-3'>
          <FormInput
            {...formProps}
            className="flex-dynamic"
            name="defaultTitle"
            onChange={(e) => handleDefaultUpdated({ title: e.target.value })}
          />
          <LayoutButtonGroupForm
            {...formProps}
            config={state.defaultTitleSize}
            name="defaultTitleSize"
            options={[{ id: "xs", name: "XS" },{ id: "sm", name: "S" },{ id: "md", name: "M" },{ id: "lg", name: "L" },{ id: "xl", name: "XL" }]}
            onContentChange={(content) => handleDefaultUpdated({ titleSize: content})}  
          />
        </SplitField>
        <Label className="px-3 pt-2 flex-static">Body Text:</Label>
        <SplitField className="px-3 pb-3">
          <FormInput
            {...formProps}
            type="textarea"
            className="flex-dynamic"
            name="defaultBody"
            maxChars={65535}
            onChange={(e) => handleDefaultUpdated({ body: e.target.value })}
          />
          <LayoutButtonGroupForm
            {...formProps}
            config={state.defaultBodySize}
            name="defaultBodySize"
            options={[{ id: "xs", name: "XS" },{ id: "sm", name: "S" },{ id: "md", name: "M" },{ id: "lg", name: "L" },{ id: "xl", name: "XL" }]}
            onContentChange={(content) => handleDefaultUpdated({ bodySize: content})}  
          />
        </SplitField>
        <div className="px-3 ">
          <LayoutColorForm
            className="px-3"
            config={state.defaultFontColor}
            onContentChange={(content) => handleDefaultUpdated({fontColor: content})}
          />
        </div>
      </div>
      <div className={`w-100 ${type === "video" ? "d-flex align-items-center" : "d-none"}`}>
        <Label className="ps-3 pt-2 flex-static">YouTube Video URL: </Label>
        <FormInput
          {...formProps}
          className="p-3 flex-dynamic"
          name="videoUrl"
          placeholder="https://www.youtube.com/watch?v=..."
          maxChars={1024}
        />
      </div>
      <div className={`w-100 ${type === "html" ? "d-flex align-items-center" : "d-none"}`}>
        <div className="py-3 w-100 d-flex justify-content-center">
          <Button color="success" onClick={() => setModalOpen(true)}>
            Edit HTML
          </Button>
        </div>
        <ModalTextEditor
          isOpen={modalOpen}
          toggle={() => {}}
          initialHTML={html}
          onUpdate={(html) => setValue(htmlKey, html)}
          onDone={() => setModalOpen(false)}
        />
      </div>
    </div>
  );
};

export default PostLoginPageSubstep;